import type fr from '../fr/common';

const en: typeof fr = {
  appName: 'Ménage Éclat',
  actions: {
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    confirm: 'Confirm',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    create: 'Create',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    view: 'View',
    manage: 'Manage',
    message: 'Message',
    search: 'Search',
    filter: 'Filter',
    retry: 'Retry',
    logout: 'Sign out',
    login: 'Sign in',
    register: 'Create an account',
    seeAll: 'See all',
    loadMore: 'Show more',
    today: 'Today',
    directions: 'Directions',
    openDirections: 'Open directions',
    send: 'Send',
    apply: 'Apply',
    reset: 'Reset',
    copy: 'Copy',
  },
  labels: {
    loading: 'Loading…',
    optional: 'optional',
    yes: 'Yes',
    no: 'No',
    none: 'None',
    all: 'All',
    total: 'Total',
    date: 'Date',
    time: 'Time',
    status: 'Status',
    address: 'Address',
    client: 'Client',
    employee: 'Employee',
    team: 'Team',
    sector: 'Sector',
    duration: 'Duration',
    price: 'Price',
    notes: 'Notes',
    phone: 'Phone',
    email: 'Email',
    language: 'Language',
    amount: 'Amount',
    minutes: '{{count}} min',
    page: 'Page {{page}} of {{pages}}',
    results: '{{count}} result(s)',
    perVisit: 'per visit',
    notifications: 'Notifications',
    noNotifications: 'No notifications.',
    markAllRead: 'Mark all as read',
    devSwitcher: 'DEV: switch role',
    devSwitcherHint: 'Visible in development only.',
    more: 'More',
  },
  roles: { OWNER: 'Owner', ADMIN: 'Administrator', SUPERVISOR: 'Supervisor', EMPLOYEE: 'Employee', CLIENT: 'Client' },
  jobStatus: {
    DRAFT: 'Draft',
    UNASSIGNED: 'To assign',
    ASSIGNED: 'Assigned',
    CONFIRMED: 'Confirmed',
    EN_ROUTE: 'On the way',
    ARRIVED: 'Arrived',
    IN_PROGRESS: 'In progress',
    COMPLETED: 'Completed',
    REVIEW_REQUIRED: 'To review',
    CANCELLED: 'Cancelled',
    NO_SHOW: 'No-show',
  },
  quoteStatus: {
    DRAFT: 'Draft',
    SUBMITTED: 'Received',
    UNDER_REVIEW: 'Under review',
    PRICE_PROPOSED: 'Price proposed',
    SENT_TO_CLIENT: 'Sent',
    ACCEPTED: 'Accepted',
    REJECTED: 'Declined',
    EXPIRED: 'Expired',
    CANCELLED: 'Cancelled',
  },
  quoteStatusClient: {
    SUBMITTED: 'Being prepared',
    UNDER_REVIEW: 'Being prepared',
    PRICE_PROPOSED: 'Being prepared',
    SENT_TO_CLIENT: 'Price available',
  },
  paymentStatus: {
    PENDING: 'Due',
    AUTHORIZED: 'Authorized',
    PAID: 'Paid',
    FAILED: 'Failed',
    REFUNDED: 'Refunded',
    PARTIALLY_REFUNDED: 'Partially refunded',
    CANCELLED: 'Cancelled',
  },
  payrollStatus: { PENDING: 'Pending', APPROVED: 'Approved', PAID: 'Paid', VOID: 'Void' },
  conversationStatus: {
    OPEN: 'Open',
    WAITING_CLIENT: 'Awaiting reply',
    WAITING_INTERNAL: 'Waiting (internal)',
    RESOLVED: 'Resolved',
    ARCHIVED: 'Archived',
  },
  agreementStatus: { ACTIVE: 'Active', PAUSED: 'Paused', CANCELLED: 'Cancelled', ENDED: 'Ended' },
  frequency: { ONE_TIME: 'One time', WEEKLY: 'Every week', BIWEEKLY: 'Every two weeks', MONTHLY: 'Every month', CUSTOM: 'Custom' },
  contractTerm: { NONE: 'No contract', '6_MONTH': '6-month contract', '12_MONTH': '12-month contract' },
  billing: { ONE_TIME: 'Pay per visit', RECURRING_MONTHLY: 'Monthly', CONTRACT_6_MONTH: '6-month contract', CONTRACT_12_MONTH: '12-month contract' },
  propertyType: { HOUSE: 'House', CONDO: 'Condo', APARTMENT: 'Apartment', COMMERCIAL: 'Commercial', OTHER: 'Other' },
  section: { KITCHEN: 'Kitchen', BATHROOM: 'Bathroom', BEDROOMS: 'Bedrooms', LIVING: 'Living room', COMMON: 'Common areas', OTHER: 'Other' },
  priority: { LOW: 'Low', NORMAL: 'Normal', HIGH: 'High', URGENT: 'Urgent' },
  compensation: { FIXED_PER_JOB: 'Fixed amount per job', HOURLY: 'Hourly rate', PERCENTAGE: 'Share of price', CUSTOM: 'Custom' },
  accessKind: { DOOR_CODE: 'Door code', KEY: 'Key', ALARM: 'Alarm', LOCKBOX: 'Lockbox', CONCIERGE: 'Concierge', OTHER: 'Other' },
  weekday: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  weekdayShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  errors: {
    generic: 'We could not save your change. Please try again in a few seconds.',
    load: 'We could not load this information. Please try again in a few seconds.',
    NETWORK: 'Cannot connect. Check your network and try again.',
    UNAUTHORIZED: 'Your session expired. Please sign in again.',
    INVALID_CREDENTIALS: 'Invalid email or password.',
    ACCOUNT_DISABLED: 'This account is disabled.',
    EMAIL_TAKEN: 'An account already exists with this email.',
    FORBIDDEN: 'You do not have access to this action.',
    MISSING_PERMISSION: 'You do not have the permission required for this action.',
    NOT_FOUND: 'Item not found.',
    VALIDATION_ERROR: 'Some information is invalid. Please check the form.',
    RATE_LIMITED: 'Too many attempts. Please wait a minute.',
    CUTOFF_PASSED: 'The change deadline has passed. Send us a message for any request.',
    CHECKLIST_INCOMPLETE: 'Some required tasks are not checked.',
    INVALID_TRANSITION: 'This step is not possible right now.',
    ASSIGNMENT_CONFLICT: 'This assignment creates a conflict.',
    OVERRIDE_REASON_REQUIRED: 'Please give the reason for the override.',
    JOB_NOT_MODIFIABLE: 'This cleaning can no longer be changed.',
    DATE_IN_PAST: 'Pick a future date.',
    ALREADY_PAID: 'This payment is already settled.',
    PAYROLL_INVALID_STATUS: 'Some lines cannot move to this status.',
    QUOTE_NOT_EDITABLE: 'This quote can no longer be changed.',
    CANNOT_GRANT_UNHELD: 'You cannot grant a permission you do not hold.',
    OWNER_PROTECTED: 'Only an owner can change this account.',
    SECTOR_IN_USE: 'This sector still contains properties.',
    JOB_NOT_STARTED: 'Start the cleaning before checking tasks.',
    ACCESS_INFO_RESTRICTED: 'Access information is visible 24 h before the cleaning.',
    AGREEMENT_NOT_ACTIVE: "This service is not active.",
    AGREEMENT_NOT_PAUSED: "This service is not paused.",
    AGREEMENT_CLOSED: "This service has already ended or been cancelled.",
    AGREEMENT_BILLED_PER_VISIT: "This service is paid per visit.",
    JOB_CANCELLED: "This cleaning is cancelled.",
    CONFIRM_EMAIL: "Account created. Confirm your email with the link we sent, then sign in.",
    STAFF_ONLY_ACTION: "This action must be done by the team.",
    INVALID_AVAILABILITY: "An availability window is invalid (end before start).",
    LEADER_NOT_MEMBER: "The team leader must be a team member.",
    NOT_REFUNDABLE: "This payment cannot be refunded.",
    CANNOT_EDIT_SELF: "You cannot edit your own account here.",
    OWNER_ONLY: "Owner only.",
    ROLE_NOT_EDITABLE: "This role cannot be edited.",
    INVALID_RANGE: "The end date must follow the start date.",
    SERVICE_TYPE_NOT_FOUND: "Service type not found.",
    PRICE_CHANGE_NOT_ALLOWED: "You cannot change a price.",
    JOB_NOT_ASSIGNABLE: "This cleaning can no longer be assigned.",
    EMPLOYEE_ONLY: "Employees only.",
    NOT_YOUR_PROPERTY: "This property is not yours.",
  },
  empty: { generic: 'Nothing to show yet.' },
};
export default en;
